import { html } from "lit";
import { t } from "../../i18n/index.ts";
import type { AppViewState } from "../app-view-state.ts";
import { icons } from "../icons.ts";
import { normalizeBasePath } from "../navigation.ts";
import { agentLogoUrl } from "./agents-utils.ts";

export function renderLoginGate(state: AppViewState) {
  const basePath = normalizeBasePath(state.basePath ?? "");
  const faviconSrc = agentLogoUrl(basePath);
  const hasError = Boolean(state.lastError || state.lastErrorCode);

  // 连接中：显示 loading，不显示表单
  if (!hasError) {
    return html`
      <div class="login-gate" style="display:flex;align-items:center;justify-content:center;height:100%;">
        <div style="text-align:center;">
          <img src=${faviconSrc} alt="" style="width:48px;height:48px;border-radius:10px;margin-bottom:16px;" />
          <div style="font-size:15px;color:var(--text-secondary,#6B6560);">正在连接设备...</div>
          <div style="margin-top:12px;">
            <svg width="24" height="24" viewBox="0 0 24 24" style="animation:spin 1s linear infinite;">
              <circle cx="12" cy="12" r="10" stroke="var(--border,#e5e0d8)" stroke-width="3" fill="none"/>
              <path d="M12 2a10 10 0 0 1 10 10" stroke="var(--accent,#D97706)" stroke-width="3" fill="none" stroke-linecap="round"/>
            </svg>
          </div>
          <button
            style="margin-top:20px;padding:8px 24px;border:1px solid var(--border,#e5e0d8);border-radius:8px;background:transparent;color:var(--text-secondary,#6B6560);cursor:pointer;font-size:13px;"
            @click=${() => {
              try { window.parent.postMessage({ type: "unbind-device" }, "*"); } catch {}
            }}
          >返回</button>
          <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
        </div>
      </div>
    `;
  }

  // 连接失败：显示表单
  return html`
    <div class="login-gate">
      <div class="login-gate__card">
        <div class="login-gate__header">
          <img class="login-gate__logo" src=${faviconSrc} alt="龙虾智盒" />
          <div class="login-gate__title">龙虾智盒</div>
          <div class="login-gate__sub">${t("login.subtitle")}</div>
        </div>
        <div class="login-gate__form">
          <label class="field">
            <span>${t("overview.access.wsUrl")}</span>
            <input
              .value=${state.settings.gatewayUrl}
              @input=${(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                state.applySettings({ ...state.settings, gatewayUrl: v });
              }}
              placeholder="ws://127.0.0.1:18789"
            />
          </label>
          <label class="field">
            <span>${t("overview.access.token")}</span>
            <div class="login-gate__secret-row">
              <input
                type=${state.loginShowGatewayToken ? "text" : "password"}
                autocomplete="off"
                spellcheck="false"
                .value=${state.settings.token}
                @input=${(e: Event) => {
                  const v = (e.target as HTMLInputElement).value;
                  state.applySettings({ ...state.settings, token: v });
                }}
                placeholder="OPENCLAW_GATEWAY_TOKEN (${t("login.passwordPlaceholder")})"
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === "Enter") {
                    state.connect();
                  }
                }}
              />
              <button
                type="button"
                class="btn btn--icon ${state.loginShowGatewayToken ? "active" : ""}"
                title=${state.loginShowGatewayToken ? "Hide token" : "Show token"}
                aria-label="Toggle token visibility"
                aria-pressed=${state.loginShowGatewayToken}
                @click=${() => {
                  state.loginShowGatewayToken = !state.loginShowGatewayToken;
                }}
              >
                ${state.loginShowGatewayToken ? icons.eye : icons.eyeOff}
              </button>
            </div>
          </label>
          <label class="field">
            <span>${t("overview.access.password")}</span>
            <div class="login-gate__secret-row">
              <input
                type=${state.loginShowGatewayPassword ? "text" : "password"}
                autocomplete="off"
                spellcheck="false"
                .value=${state.password}
                @input=${(e: Event) => {
                  const v = (e.target as HTMLInputElement).value;
                  state.password = v;
                }}
                placeholder="${t("login.passwordPlaceholder")}"
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === "Enter") {
                    state.connect();
                  }
                }}
              />
              <button
                type="button"
                class="btn btn--icon ${state.loginShowGatewayPassword ? "active" : ""}"
                title=${state.loginShowGatewayPassword ? "Hide password" : "Show password"}
                aria-label="Toggle password visibility"
                aria-pressed=${state.loginShowGatewayPassword}
                @click=${() => {
                  state.loginShowGatewayPassword = !state.loginShowGatewayPassword;
                }}
              >
                ${state.loginShowGatewayPassword ? icons.eye : icons.eyeOff}
              </button>
            </div>
          </label>
          <button
            class="btn primary login-gate__connect"
            @click=${() => state.connect()}
          >
            ${t("common.connect")}
          </button>
          <button
            class="btn login-gate__back"
            style="margin-top:8px;width:100%;padding:10px;border:1px solid var(--border,#e5e0d8);border-radius:var(--radius-md,8px);background:transparent;color:var(--text-secondary,#6B6560);cursor:pointer;font-size:14px;"
            @click=${() => {
              try { window.parent.postMessage({ type: "unbind-device" }, "*"); } catch {}
            }}
          >
            返回
          </button>
        </div>
        ${
          state.lastError
            ? html`<div class="callout danger" style="margin-top: 14px;">
                <div>${state.lastError}</div>
              </div>`
            : ""
        }
        <div class="login-gate__help">
          <div class="login-gate__help-title">${t("overview.connection.title")}</div>
          <ol class="login-gate__steps">
            <li>${t("overview.connection.step1")}<code>龙虾智盒 gateway run</code></li>
            <li>${t("overview.connection.step2")}<code>龙虾智盒 dashboard --no-open</code></li>
            <li>${t("overview.connection.step3")}</li>
          </ol>
          <div class="login-gate__docs">
            <a
              class="session-link"
              href="https://docs.openclaw.ai/web/dashboard"
              target="_blank"
              rel="noreferrer"
            >${t("overview.connection.docsLink")}</a>
          </div>
        </div>
      </div>
    </div>
  `;
}
